import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { toast } from 'react-toastify';

import {
  getUserwoolfActivities,
  getUserProjectSubmissions,
  generateProjectSubmissionLink,
  getUserQuizSubmissions,
  getUserPeerReviewSubmissions,
  sendMeetingToWoolf,
  sendQuizToWoolf,
  sendProjectToWoolf,
  sendPeerReviewToWoolf
} from '../../../../../services/apiService';
import { useLocation } from 'react-router-dom';

/* ================= Tabs ================= */

const TABS = [
  { key: 'meeting', label: 'Meeting' },
  { key: 'project', label: 'Project' },
  { key: 'peerReview', label: 'Peer Review' },
  { key: 'concept', label: 'Concept' },
  { key: 'quiz', label: 'Quiz' },
];

/* ================= Utils ================= */

const getCategoryIdFromExternalId = (externalId = '') => {
  const parts = externalId.split('-');
  return parts.length >= 3 ? parts[1] : null;
};

/* ================= Component ================= */

export default function AuditLogs() {
  const location = useLocation();

  const userParams = location.state.userParams;

  const courseType = userParams?.UserSubscriptions_courseType;
  const userId = userParams?.id;

  const [allData, setAllData] = useState([]);
  const [activeTab, setActiveTab] = useState('project');
  const [expandedRows, setExpandedRows] = useState({});
  const [allProjectSubmissions, setAllProjectSubmissions] = useState([]);
  const [allQuizSubmissions, setAllQuizSubmissions] = useState([]);
  const [allPeerReviewSubmissions, setAllPeerReviewSubmissions] = useState([]);
  const [submissionLoading, setSubmissionLoading] = useState({});
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [userJourneyId, setUserJourneyId] = useState(null);
  const [meetingChips, setMeetingChips] = useState([]);
  const [inputValue, setInputValue] = useState('');
  /* ================= Fetch ================= */

  useEffect(() => {
    fetchActivities();
  }, []);

  useEffect(() => {
    if (activeTab === 'project') {
      fetchProjectSubmissions();
    }
  }, [activeTab]);

  const fetchActivities = async () => {
    try {
      const res = await getUserwoolfActivities(courseType, userId);
      const transformed = transformActivities(res.studentWoolfActivities);
      setAllData(transformed);
      setUserJourneyId(res.userJourneyId);

      // Fetch quiz & peer review submissions
      await fetchQuizSubmissions(transformed);
      await fetchPeerReviewSubmissions(transformed);
    } catch (err) {
      console.error(err);
      toast.error('Failed to fetch activities');
    }
  };

  const isActivityCountInvalid = (row, matchedSubmissions) => {
    const activities = row.activities || [];
    const kinds = activities.map(a => a.kind);

    const has = (kind) => kinds.includes(kind);

    // PROJECT
    if (row.resourceType === "project") {
      if (matchedSubmissions.length === 0) return true;

      return !(
        has("submitAssignment") &&
        has("addGrade") &&
        has("addFeedback")
      );
    }
    // PEER REVIEW
    if (row.resourceType === "quiz") {
      if (matchedSubmissions.length === 0) return true;
      return !(
        has("submitAssignment") &&
        has("addGrade")
      );
    }
    // QUIZ
    if (row.resourceType === "peerReview") {
      if (matchedSubmissions.length === 0) return true;

      return !has("submitAssignment");
    }
    // MEETING & CONCEPT
    if (row.resourceType === "meeting" || row.resourceType === "concept") {
      return activities.length !== 1;
    }
    // Other resource types → valid
    return false;
  };

  const fetchProjectSubmissions = async () => {
    try {
      setRefreshLoading(true);
      const res = await getUserProjectSubmissions(courseType, userId);
      setAllProjectSubmissions(res || []);
    } catch (err) {
      console.error(err);
      toast.error('Failed to fetch project submissions');
    } finally {
      setRefreshLoading(false);
    }
  };

  const fetchQuizSubmissions = async (activities ) => {
    try {
      const safeActivities = Array.isArray(activities)
        ? activities
        : allData;

      const quizExternalIds = safeActivities
        .filter((row) => row.resourceType === 'quiz')
        .map((row) => row.externalId);

      if (quizExternalIds.length === 0) return;

      const res = await getUserQuizSubmissions({
        courseType,
        userId,
        externalIds: quizExternalIds,
      });

      setAllQuizSubmissions(res || []);
    } catch (err) {
      console.error(err);
      toast.error('Failed to fetch quiz submissions');
    }
  };

  const fetchPeerReviewSubmissions = async (activities ) => {
    try {
      const safeActivities = Array.isArray(activities)
        ? activities
        : allData;
      const peerReviewExternalIds = safeActivities
        .filter((row) => row.resourceType === 'peerReview')
        .map((row) => row.externalId);

      if (peerReviewExternalIds.length === 0) return;

      const res = await getUserPeerReviewSubmissions({
        courseType,
        userId,
        externalIds: peerReviewExternalIds,
      });

      setAllPeerReviewSubmissions(res || []);
    } catch (err) {
      console.error(err);
      toast.error('Failed to fetch peer review submissions');
    }
  };

  const handleGenerateSubmission = async (project) => {
    try {
      setSubmissionLoading((prev) => ({
        ...prev,
        [project.projectId]: true,
      }));

      await generateProjectSubmissionLink({
        projects: [
          {
            projectId: String(project.projectId),
            projectCategoryId: String(project.projectCategoryId),
          },
        ],
        courseType,
        userId,
      });

      toast.success('Submission generated successfully');
      await fetchProjectSubmissions();
    } catch (err) {
      console.error(err);
      toast.error('Failed to generate submission');
    } finally {
      setSubmissionLoading((prev) => ({
        ...prev,
        [project.projectId]: false,
      }));
    }
  };

  /* ================= Transform ================= */

  const transformActivities = (studentWoolfActivities = {}) => {
    const rows = [];

    Object.entries(studentWoolfActivities).forEach(([type, resources]) => {
      resources.forEach((resource) => {
        rows.push({
          resourceId: resource.resourceId,
          externalId: resource.externalId,
          courseName: resource.courseName,
          resourceType: type,
          resourceName: resource.resourceName || '-',
          activities: resource.activities || [],
        });
      });
    });

    return rows;
  };

  // ✅ ADD BELOW EXISTING STATES
  const [selectedRows, setSelectedRows] = useState({
    meeting: [],
    project: [],
    quiz: [],
    peerReview: [],
  });

  /* ================= Selection Logic ================= */

  const toggleSelectRow = (row, e) => {
    e.stopPropagation();

    setSelectedRows((prev) => {
      const current = prev[activeTab] || [];
      const exists = current.find((r) => r.resourceId === row.resourceId);

      return {
        ...prev,
        [activeTab]: exists
          ? current.filter((r) => r.resourceId !== row.resourceId)
          : [...current, row],
      };
    });
  };

  const selectAllCurrentTab = () => {
    const rows = allData.filter((r) => r.resourceType === activeTab);
    setSelectedRows((prev) => ({ ...prev, [activeTab]: rows }));
  };

  const clearSelectionCurrentTab = () => {
    setSelectedRows((prev) => ({ ...prev, [activeTab]: [] }));
  };

  const handleSendToWoolf = async () => {
    try {
      if (!selectedRows[activeTab]?.length && meetingChips.length === 0) {
        toast.warn(`No ${activeTab} records selected`);
        return;
      }

      if (!userJourneyId) {
        toast.error('User journey not loaded');
        return;
      }

      const ids = (selectedRows[activeTab] || []).map(
        (row) => `${row.externalId}`
      );

      let payload = {};
      let response;

      switch (activeTab) {
        case 'meeting':
          payload = { meetingIds: [...ids, ...meetingChips], courseType, userId, userJourneyId };
          response = await sendMeetingToWoolf(payload);
          break;

        case 'project':
          payload = { projectIds: ids, courseType, userId, userJourneyId };
          response = await sendProjectToWoolf(payload);
          break;

        case 'quiz':
          payload = { quizIds: ids, courseType, userId, userJourneyId };
          response = await sendQuizToWoolf(payload);
          break;

        case 'peerReview':
          payload = { peerReviewIds: ids, courseType, userId, userJourneyId };
          response = await sendPeerReviewToWoolf(payload);
          break;

        default:
          toast.error('Invalid tab selected');
          return;
      }

      console.log('📤 Send to Woolf Payload:', payload);
      console.log('📤 Woolf Response:', response);

      toast.success(`Sent ${ids.length} ${activeTab} records to Woolf`);
    } catch (err) {
      console.error('❌ Error sending to Woolf:', err);
      toast.error(`Failed to send ${activeTab} records to Woolf`);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      setMeetingChips([...meetingChips, inputValue.trim()]);
      setInputValue('');
    }
  };

  // Remove a chip
  const removeChip = (index) => {
    setMeetingChips(meetingChips.filter((_, i) => i !== index));
  };

  /* ================= Logic ================= */

  const toggleRow = (resourceId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [resourceId]: !prev[resourceId],
    }));
  };

  const displayData =
    activeTab === 'all'
      ? allData
      : allData.filter((row) => row.resourceType === activeTab);

  const getMatchedSubmissions = (row) => {
    let categoryId;
    if(row.resourceType === "project") {
       categoryId = getCategoryIdFromExternalId(row.externalId);
    }
    if(row.resourceType !== "project") {
      categoryId = row.externalId.split('-')[0];
    }
    switch (row.resourceType) {
      case 'project':
        return allProjectSubmissions.filter(
          (p) => String(p.projectCategoryId) === String(categoryId)
        );
      case 'quiz':
        return allQuizSubmissions.filter(
          (q) => String(q.lessonId) === String(categoryId)
        );

      case 'peerReview':
        // ✅ MATCH USING lessonId
        return allPeerReviewSubmissions.filter(
          (p) => String(p.lessonId) === String(categoryId)
        );
      default:
        return [];
    }
  };

  /* ================= Render ================= */

  return (
    <div style={{ padding: 20 }}>
      {/* ================= Tabs ================= */}
      <div
        style={{
          display: 'flex',
          gap: 10,
          marginBottom: 16,
          justifyContent: 'center', // ✅ center horizontally
          alignItems: 'center', // ✅ vertical alignment (safe)
          flexWrap: 'wrap', // ✅ responsive on small screens
        }}
      >
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => {
              setActiveTab(tab.key);
              setExpandedRows({});
            }}
            style={{
              padding: '8px 14px',
              borderRadius: 6,
              border: '1px solid var(--primary-main, #1976d2)',
              cursor: 'pointer',
              background: activeTab === tab.key ? 'var(--primary-main, #1976d2)' : 'var(--card-background, #fff)',
              color: activeTab === tab.key ? '#fff' : 'var(--text-primary, #000)',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {activeTab !== 'concept' && (
        <div
          style={{
            marginBottom: 16,
            display: 'flex',
            gap: 12,
            justifyContent: 'center',
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          {/* Select All */}
          <button
            onClick={selectAllCurrentTab}
            style={{
              padding: '8px 14px',
              borderRadius: 6,
              border: '1px solid var(--primary-main, #1976d2)',
              cursor: 'pointer',
              background: 'var(--card-background, #fff)',
              color: 'var(--text-primary, #000)',
            }}
          >
            Select All
          </button>

          {/* Clear */}
          <button
            onClick={clearSelectionCurrentTab}
            style={{
              padding: '8px 14px',
              borderRadius: 6,
              border: '1px solid var(--primary-main, #1976d2)',
              cursor: 'pointer',
              background: 'var(--card-background, #fff)',
              color: 'var(--text-primary, #000)',
            }}
          >
            Clear
          </button>

          {/* Send to Woolf */}
          <button
            onClick={handleSendToWoolf}
            disabled={
              activeTab === 'meeting'
                ? !selectedRows[activeTab]?.length && !meetingChips.length
                : !selectedRows[activeTab]?.length
            }
            style={{
              padding: '8px 14px',
              borderRadius: 6,
              border: '1px solid var(--primary-main, #1976d2)',
              cursor:
                activeTab === 'meeting'
                  ? selectedRows[activeTab]?.length || meetingChips.length
                    ? 'pointer'
                    : 'not-allowed'
                  : selectedRows[activeTab]?.length
                  ? 'pointer'
                  : 'not-allowed',
              background:
                activeTab === 'meeting'
                  ? selectedRows[activeTab]?.length || meetingChips.length
                    ? 'var(--primary-main, #1976d2)'
                    : 'var(--border-light, #e0e0e0)'
                  : selectedRows[activeTab]?.length
                  ? 'var(--primary-main, #1976d2)'
                  : 'var(--border-light, #e0e0e0)',
              color:
                activeTab === 'meeting'
                  ? selectedRows[activeTab]?.length || meetingChips.length
                    ? '#fff'
                    : 'var(--text-muted, #777)'
                  : selectedRows[activeTab]?.length
                  ? '#fff'
                  : 'var(--text-muted, #777)',
            }}
          >
            Send to Woolf
          </button>

          {activeTab === 'meeting' && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flexWrap: 'wrap',
                marginBottom: 12,
              }}
            >
              {/* Text input */}
              <input
                type='text'
                placeholder='Enter meeting...'
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                style={{
                  padding: '6px 10px',
                  borderRadius: 6,
                  border: '1px solid var(--primary-main, #1976d2)',
                  flex: 1,
                  minWidth: 200,
                  background: 'var(--card-background, #fff)',
                  color: 'var(--text-primary, #000)',
                }}
              />

              {/* Chips */}
              {meetingChips.map((chip, index) => (
                <div
                  key={index}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '4px 10px',
                    borderRadius: 16,
                    background: 'var(--primary-main, #1976d2)',
                    color: '#fff',
                    fontSize: 12,
                    cursor: 'pointer',
                  }}
                  onClick={() => removeChip(index)}
                >
                  {chip} &times;
                </div>
              ))}
            </div>
          )}
          {/* Refresh Buttons (Tab Specific) */}
          {activeTab === 'project' && (
            <button
              onClick={fetchProjectSubmissions}
              disabled={refreshLoading}
              style={{
                padding: '8px 14px',
                borderRadius: 6,
                border: '1px solid var(--primary-main, #1976d2)',
                background: 'var(--primary-main, #1976d2)',
                color: '#fff',
                cursor: refreshLoading ? 'not-allowed' : 'pointer',
              }}
            >
              {refreshLoading ? 'Refreshing...' : 'Refresh Project Submissions'}
            </button>
          )}

          {activeTab === 'quiz' && (
            <button
              onClick={fetchQuizSubmissions}
              disabled={refreshLoading}
              style={{
                padding: '8px 14px',
                borderRadius: 6,
                border: '1px solid var(--primary-main, #1976d2)',
                background: 'var(--primary-main, #1976d2)',
                color: '#fff',
                cursor: refreshLoading ? 'not-allowed' : 'pointer',
              }}
            >
              {refreshLoading ? 'Refreshing...' : 'Refresh Quiz Submissions'}
            </button>
          )}

          {activeTab === 'peerReview' && (
            <button
              onClick={fetchPeerReviewSubmissions}
              disabled={refreshLoading}
              style={{
                padding: '8px 14px',
                borderRadius: 6,
                border: '1px solid var(--primary-main, #1976d2)',
                background: 'var(--primary-main, #1976d2)',
                color: '#fff',
                cursor: refreshLoading ? 'not-allowed' : 'pointer',
              }}
            >
              {refreshLoading
                ? 'Refreshing...'
                : 'Refresh PeerReview Submissions'}
            </button>
          )}
        </div>
      )}

      {/* ================= Table ================= */}
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: 'var(--input-background, #eee)' }}>
            {activeTab !== 'concept' && <th style={th}>Select</th>}
            <th style={th}>External ID</th>
            <th style={th}>Type</th>
            <th style={th}>Course Name</th>
            <th style={th}>Resource Name</th>
            <th style={th}>Total Activities</th>
          </tr>
        </thead>

        <tbody>
          {displayData.map((row) => {
            const matchedSubmissions = getMatchedSubmissions(row);

            return (
              <React.Fragment key={row.resourceId}>
                <tr
                  onClick={() => toggleRow(row.resourceId)}
                  style={{
                    cursor: 'pointer',
                    background: isActivityCountInvalid(row,matchedSubmissions)
                      ? '#f28b82'
                      : '#85dc9a',
                  }}
                >
                  {activeTab !== 'concept' && (
                    <td style={td} onClick={(e) => e.stopPropagation()}>
                      <input
                        type='checkbox'
                        checked={
                          selectedRows[activeTab]?.some(
                            (r) => r.resourceId === row.resourceId
                          ) || false
                        }
                        onChange={(e) => toggleSelectRow(row, e)}
                      />
                    </td>
                  )}
                  <td style={td}>{row.externalId}</td>
                  <td style={td}>{row.resourceType}</td>
                  <td style={td}>{row.courseName}</td>
                  <td style={td}>{row.resourceName}</td>
                  <td style={td}>{row.activities.length}</td>
                </tr>

                {expandedRows[row.resourceId] && (
                  <tr>
                    <td colSpan={5} style={{ padding: 0 }}>
                      {/* Activities */}
                      <table style={innerTable}>
                        <thead>
                          <tr style={{ background: 'var(--input-background, #ddd)' }}>
                            <th style={subTh}>Activity</th>
                            <th style={subTh}>Date</th>
                            <th style={subTh}>Name</th>
                            <th style={subTh}>Score</th>
                            <th style={subTh}>Content</th>
                          </tr>
                        </thead>
                        <tbody>
                          {row.activities.map((a) => (
                            <tr key={a.id}>
                              <td style={subTd}>{a.kind}</td>
                              <td style={subTd}>
                                {a.created
                                  ? moment(a.created).format(
                                      'DD MMM YYYY, HH:mm'
                                    )
                                  : '-'}
                              </td>
                              <td style={subTd}>{a.resourceName || '-'}</td>
                              <td style={subTd}>{a.studentScore ?? '-'}</td>
                              <td
                                style={subTd}
                                dangerouslySetInnerHTML={{
                                  __html: a.data?.content || a.content || '-',
                                }}
                              />
                            </tr>
                          ))}
                        </tbody>
                      </table>

                      {/* Submissions */}
                      {['project'].includes(row.resourceType) && (
                        <div style={projectBox}>
                          <b>{row.resourceType} Submissions</b>

                          {matchedSubmissions.length === 0 && (
                            <div>No submission found</div>
                          )}

                          {matchedSubmissions.map((p, i) => (
                            <div key={i} style={projectCard}>
                              {row.resourceType === 'project' && (
                                <>
                                  <div>
                                    <b>Project ID:</b> {p.projectId}
                                  </div>
                                  <div>
                                    <b>Category:</b> {p.projectCategoryId}
                                  </div>
                                  <div style={{ marginTop: 8 }}>
                                    <b>Changes:</b>
                                    <div>{p.solution?.changes || '-'}</div>
                                  </div>
                                  {p.solution?.codeLink && (
                                    <div style={{ marginTop: 4 }}>
                                      <b>Code Link:</b>{' '}
                                      <a
                                        href={p.solution.codeLink}
                                        target='_blank'
                                        rel='noreferrer'
                                      >
                                        {p.solution.codeLink}
                                      </a>
                                    </div>
                                  )}
                                  {p.solution?.videoLink && (
                                    <div style={{ marginTop: 4 }}>
                                      <b>Video Link:</b>{' '}
                                      <a
                                        href={p.solution.videoLink}
                                        target='_blank'
                                        rel='noreferrer'
                                      >
                                        {p.solution.videoLink}
                                      </a>
                                    </div>
                                  )}
                                  {p.submissionLink ? (
                                    <div style={{ marginTop: 8 }}>
                                      <b>Submission :</b>{' '}
                                      <a
                                        href={p.submissionLink}
                                        target='_blank'
                                        rel='noreferrer'
                                      >
                                        {p.submissionLink}
                                      </a>
                                    </div>
                                  ) : (
                                    <div style={{ marginTop: 8 }}>
                                      <button
                                        onClick={() =>
                                          handleGenerateSubmission(p)
                                        }
                                        disabled={
                                          submissionLoading[p.projectId]
                                        }
                                        style={{
                                          padding: '6px 12px',
                                          borderRadius: 4,
                                          border: '1px solid var(--primary-main, #1976d2)',
                                          background: 'var(--primary-main, #1976d2)',
                                          color: '#fff',
                                          cursor: 'pointer',
                                        }}
                                      >
                                        {submissionLoading[p.projectId]
                                          ? 'Generating...'
                                          : 'Generate Submission'}
                                      </button>
                                    </div>
                                  )}
                                </>
                              )}

                              {['quiz', 'peerReview'].includes(
                                row.resourceType
                              ) && (
                                <>
                                  <div>
                                    <b>ID:</b> {p.quizId || p.reviewId}
                                  </div>
                                  <div>
                                    <b>Category:</b>{' '}
                                    {p.quizCategoryId || p.reviewCategoryId}
                                  </div>
                                  <div>
                                    <b>Status:</b> {p.status || '-'}
                                  </div>
                                  <div>
                                    <b>Score:</b> {p.maxScore ?? '-'}
                                  </div>
                                  <div>
                                    <b>ReportKey:</b>{' '}
                                    {p.reportKey && (
                                      <a
                                        href={`https://your-cdn.com/${p.reportKey}`}
                                        target='_blank'
                                        rel='noreferrer'
                                      >
                                        {p.reportKey}
                                      </a>
                                    )}
                                  </div>
                                </>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      {['quiz', 'peerReview'].includes(row.resourceType) && (
                        <div style={projectBox}>
                          <b>
                            {row.resourceType === 'quiz'
                              ? 'Quiz Submissions'
                              : 'Peer Review Submissions'}
                          </b>

                          {matchedSubmissions.length === 0 && (
                            <div>No submission found</div>
                          )}

                          {matchedSubmissions.map((p, i) => (
                            <div key={i} style={projectCard}>
                              <div>
                                <b>ID:</b> {p.assessmentId || p.reviewId || '-'}
                              </div>

                              <div>
                                <b>Lesson ID:</b> {p.lessonId || '-'}
                              </div>

                              <div>
                                <b>Status:</b> {p.status || '-'}
                              </div>

                              <div>
                                <b>Result:</b> {p.passOrFail || '-'}
                              </div>

                              <div>
                                <b>Score:</b> {p.maxScore ?? '-'}
                              </div>

                              <div>
                                <b>Attempted On:</b>{' '}
                                {p.createdAt
                                  ? moment(p.createdAt).format(
                                      'DD MMM YYYY, HH:mm'
                                    )
                                  : '-'}
                              </div>

                              {p.reportKey && (
                                <div style={{ marginTop: 8 }}>
                                  <b>ReportKey:</b>{' '}
                                  <a
                                    href={`https://your-cdn.com/${p.reportKey}`}
                                    target='_blank'
                                    rel='noreferrer'
                                  >
                                    {p.reportKey}
                                  </a>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}

          {displayData.length === 0 && (
            <tr>
              <td colSpan={5} style={{ textAlign: 'center', padding: 20 }}>
                No data available
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

/* ================= Styles ================= */

const th = { border: '1px solid var(--border-light, #ccc)', padding: 8, color: 'var(--text-primary, inherit)' };
const td = { border: '1px solid var(--border-light, #ccc)', padding: 8, color: 'var(--text-primary, inherit)' };

const innerTable = {
  width: '100%',
  borderCollapse: 'collapse',
  background: 'var(--input-background, #fafafa)',
};

const subTh = { border: '1px solid var(--border-light, #ccc)', padding: 6, color: 'var(--text-primary, inherit)' };
const subTd = { border: '1px solid var(--border-light, #ccc)', padding: 6, color: 'var(--text-primary, inherit)' };

const projectBox = {
  padding: 12,
  background: 'var(--primary-light, #eef5ff)',
  color: 'var(--text-primary, inherit)',
};

const projectCard = {
  marginTop: 10,
  padding: 12,
  background: 'var(--card-background, #fff)',
  border: '1px solid var(--border-light, #ccc)',
  color: 'var(--text-primary, inherit)',
};
